import { Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MetricStrip } from '../components/MetricStrip'
import { Pagination } from '../components/Pagination'
import { aggregateSpus, sortDouyinRows, type DouyinSpuAggregate } from '../douyinCommerce'
import { douyinPath, useDouyinStoreId } from '../douyinStore'
import { DouyinStatisticsBar, douyinStatisticsSearch, douyinStatisticsWindow, useDouyinStatisticsPeriod } from '../douyinStatistics'
import type { DouyinLiveAnalysis } from '../douyinTypes'
import { integer, money } from '../utils'
import { DouyinBoundary, DouyinProductImage, DySigned, DySortTh, DyTag, dyPct1 } from './douyinCommon'

type SalesFilter = 'all' | 'active' | 'zero'

const SPU_COLUMNS: Array<{ key: string; label: string; left?: boolean; className?: string }> = [
  { key: 'name', label: '商品', left: true, className: 'dy-col-prod' },
  { key: 'paid', label: '支付金额' },
  { key: 'gm', label: '货差毛利' },
  { key: 'gmr', label: '货差毛利率' },
  { key: 'items', label: '成交件数' },
  { key: 'orders', label: '成交订单' },
  { key: 'refund', label: '退款金额' },
  { key: 'skuN', label: '动销SKU数' },
  { key: 'varN', label: '抖店SKU数' },
]

export function DouyinSpuListPage() {
  return <DouyinBoundary>{(data) => <DouyinSpuListContent data={data} />}</DouyinBoundary>
}

function DouyinSpuListContent({ data }: { data: DouyinLiveAnalysis }) {
  const navigate = useNavigate()
  const storeId = useDouyinStoreId()
  const [period, setPeriod] = useDouyinStatisticsPeriod('day')
  const window = douyinStatisticsWindow(data.meta, period)
  const statisticsSearch = douyinStatisticsSearch(period)
  const [search, setSearch] = useState('')
  const [sales, setSales] = useState<SalesFilter>('all')
  const [sort, setSort] = useState({ key: 'paid', desc: true })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const all = useMemo(
    () => window ? aggregateSpus(data, window) : [],
    [data, window?.startDate, window?.endDate], // eslint-disable-line react-hooks/exhaustive-deps
  )
  const activeCount = all.filter((row) => row.active).length
  const paidTotal = all.reduce((sum, row) => sum + row.paid, 0)
  const costTotal = all.reduce((sum, row) => sum + (row.cost || 0), 0)
  const grossMargin = paidTotal - costTotal

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    return sortDouyinRows(all.filter((row) => {
      if (sales === 'active' && !row.active) return false
      if (sales === 'zero' && row.active) return false
      return !needle || row.name.toLowerCase().includes(needle) || row.pid.includes(needle)
    }), sort, (row) => row.name)
  }, [all, sales, search, sort])

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const visible = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const toggleSort = (key: string) => {
    setSort((current) => ({ key, desc: current.key === key ? !current.desc : true }))
    setPage(1)
  }
  const openDetail = (pid: string) => navigate(`${douyinPath(storeId, `/spu/${encodeURIComponent(pid)}`)}${statisticsSearch}`)

  return <div className="page-stack dy-page">
    <div className="page-title-row">
      <div><span className="dy-platform-label">DOUYIN LIVE</span><h1>SPU商品</h1><p>共 {integer(all.length)} 个SPU · 动销 {integer(activeCount)} 个</p></div>
    </div>
    <DouyinStatisticsBar period={period} onChange={(value) => { setPeriod(value); setPage(1) }} window={window} />
    <div className="pdd-caliber-note">口径说明：当前销量仅覆盖直播间成交（数据源为罗盘直播明细，{integer(data.sessions.length)}场），商城/短视频/橱窗等渠道尚未接入，不代表全店销量；货差毛利 = 用户支付金额 − 公司产品成本，运费/扣点/税/佣金/千川在场次与日级扣除，不摊到商品行；未接编码的金额不摊成本，单列「缺成本」。商品底表为抖店商品库全量（含无直播销量商品）；销量口径仍仅覆盖直播间成交。</div>
    <MetricStrip items={[
      { label: '成交金额', value: money(paidTotal), context: '窗口内直播间成交' },
      { label: '货差毛利', value: money(grossMargin), context: '支付金额 − 产品成本' },
      { label: '货差毛利率', value: dyPct1(paidTotal > 0 ? grossMargin / paidTotal : null) },
      { label: '成交件数', value: integer(all.reduce((sum, row) => sum + row.items, 0)) },
      { label: '成交订单', value: integer(all.reduce((sum, row) => sum + row.orders, 0)) },
      { label: '产品成本', value: money(costTotal) },
      { label: '缺成本金额', value: money(all.reduce((sum, row) => sum + row.nocost, 0)), context: '未接编码，不摊成本' },
      { label: '退款金额', value: money(all.reduce((sum, row) => sum + row.refund, 0)) },
    ]} />
    <section className="overview-section">
      <div className="section-heading-row"><div><h2>商品筛选</h2><p>商品名称或SPU ID</p></div><span>命中 {integer(filtered.length)} 个 · 动销 {integer(filtered.filter((row) => row.active).length)} 个</span></div>
      <div className="filter-bar">
        <label className="search-control"><Search aria-hidden="true" /><input value={search} placeholder="搜索商品名称 / SPU ID" onChange={(event) => { setSearch(event.target.value); setPage(1) }} /></label>
        <label><span className="sr-only">销量筛选</span><select value={sales} onChange={(event) => { setSales(event.target.value as SalesFilter); setPage(1) }}>
          <option value="all">销量：全部</option>
          <option value="active">仅动销</option>
          <option value="zero">无销量</option>
        </select></label>
      </div>
    </section>
    <section className="overview-section">
      <div className="table-responsive"><table className="product-table dy-table dy-spu-table"><thead><tr>
        {SPU_COLUMNS.map((column) => <DySortTh key={column.key} columnKey={column.key} label={column.label} left={column.left} className={column.className} sort={sort} onSort={toggleSort} />)}
        <th aria-label="查看详情" />
      </tr></thead><tbody>{visible.map((row) => <DouyinSpuRow key={row.pid} row={row} onOpen={() => openDetail(row.pid)} />)}</tbody></table></div>
      <p className="dy-table-footnote">共 {integer(filtered.length)} 个SPU（动销 {integer(filtered.filter((row) => row.active).length)} 个）· 支付金额合计 {money(filtered.reduce((sum, row) => sum + row.paid, 0))}</p>
      <Pagination page={currentPage} pageCount={pageCount} total={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1) }} />
    </section>
  </div>
}

function DouyinSpuRow({ row, onOpen }: { row: DouyinSpuAggregate; onOpen: () => void }) {
  return <tr className="dy-clickable-row" onClick={onOpen}>
    <td className="l dy-col-prod">
      <div className="product-link">
        <DouyinProductImage src={row.img} />
        <span className="dy-prod-main">
          <strong title={row.name}>{row.name}</strong>
          <small>
            <span>SPU · {row.pid}</span>
            {row.nocost > 0 ? <DyTag kind="nocost">缺成本</DyTag> : null}
            {!row.inCatalog ? <DyTag kind="off">已下架</DyTag> : null}
            {row.status === '商品审核未通过' ? <DyTag kind="review">审核未通过</DyTag> : null}
          </small>
        </span>
      </div>
    </td>
    <td>{row.active ? money(row.paid) : <span className="dy-faint">¥0.00</span>}</td>
    <td>{row.cost == null ? '—' : <DySigned value={row.gm} />}</td>
    <td>{dyPct1(row.gmr)}</td>
    <td>{row.active ? integer(row.items) : <span className="dy-faint">0</span>}</td>
    <td>{row.active ? integer(row.orders) : <span className="dy-faint">0</span>}</td>
    <td>{row.refund ? money(row.refund) : '—'}</td>
    <td>{integer(row.skuN)}</td>
    <td>{row.varN != null ? integer(row.varN) : '—'}</td>
    <td className="dy-chev">›</td>
  </tr>
}
